import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import * as THREE from 'three';

// --- Constants & Config ---
const ACCENT_COLOR = '#25789a';
const FORM_ENDPOINT = 'https://formspree.io/f/xgvzkajr';

// 🔴 IMPORTANT: For your live Netlify site, you MUST replace the empty string below 
// with your own Google Gemini API Key. Get one free at: https://aistudio.google.com/app/apikey
const apiKey = "";

// --- Icons (Compact Definitions) ---
const IconWrapper = ({ children, size = 24, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>
);

const Bot = (p) => <IconWrapper {...p}><rect x="3" y="11" width="18" height="10" rx="2" /><circle cx="12" cy="5" r="2" /><path d="M12 7v4" /><path d="M8 16h.01" /><path d="M16 16h.01" /></IconWrapper>;
const Cpu = (p) => <IconWrapper {...p}><rect x="4" y="4" width="16" height="16" rx="2" /><rect x="9" y="9" width="6" height="6" /><path d="M15 2v2" /><path d="M15 20v2" /><path d="M2 15h2" /><path d="M2 9h2" /><path d="M20 15h2" /><path d="M20 9h2" /><path d="M9 2v2" /><path d="M9 20v2" /></IconWrapper>;
const Workflow = (p) => <IconWrapper {...p}><rect width="8" height="8" x="3" y="3" rx="2" /><path d="M7 11v4a2 2 0 0 0 2 2h4" /><rect width="8" height="8" x="13" y="13" rx="2" /></IconWrapper>;
const Code = (p) => <IconWrapper {...p}><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></IconWrapper>;
const ChevronDown = (p) => <IconWrapper {...p}><path d="m6 9 6 6 6-6" /></IconWrapper>;
const Menu = (p) => <IconWrapper {...p}><line x1="4" x2="20" y1="12" y2="12" /><line x1="4" x2="20" y1="6" y2="6" /><line x1="4" x2="20" y1="18" y2="18" /></IconWrapper>;
const X = (p) => <IconWrapper {...p}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></IconWrapper>;
const ArrowRight = (p) => <IconWrapper {...p}><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></IconWrapper>;
const Send = (p) => <IconWrapper {...p}><path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" /></IconWrapper>;
const CheckCircle = (p) => <IconWrapper {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></IconWrapper>;
const Linkedin = (p) => <IconWrapper {...p}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" /></IconWrapper>;
const Mail = (p) => <IconWrapper {...p}><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></IconWrapper>;
const Zap = (p) => <IconWrapper {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></IconWrapper>;
const Instagram = (p) => <IconWrapper {...p}><rect width="20" height="20" x="2" y="2" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" x2="17.51" y1="6.5" y2="6.5" /></IconWrapper>;
const Search = (p) => <IconWrapper {...p}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></IconWrapper>;
const XCircle = (p) => <IconWrapper {...p}><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></IconWrapper>;
const FileText = (p) => <IconWrapper {...p}><path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" /><polyline points="14 2 14 8 20 8" /><line x1="16" x2="8" y1="13" y2="13" /><line x1="16" x2="8" y1="17" y2="17" /><line x1="10" x2="8" y1="9" y2="9" /></IconWrapper>;
const Funnel = (p) => <IconWrapper {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></IconWrapper>;
const MessageSquare = (p) => <IconWrapper {...p}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></IconWrapper>;
const ShieldCheck = (p) => <IconWrapper {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><path d="m9 12 2 2 4-4" /></IconWrapper>;
const GitBranch = (p) => <IconWrapper {...p}><line x1="6" x2="6" y1="3" y2="15" /><circle cx="18" cy="6" r="3" /><circle cx="6" cy="18" r="3" /><path d="M18 9a9 9 0 0 1-9 9" /></IconWrapper>;
const Lock = (p) => <IconWrapper {...p}><rect width="18" height="11" x="3" y="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></IconWrapper>;
const Sparkles = (p) => <IconWrapper {...p}><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z" /></IconWrapper>;
const Loader2 = (p) => <IconWrapper {...p}><path d="M21 12a9 9 0 1 1-6.219-8.56" /></IconWrapper>;
const Lightbulb = (p) => <IconWrapper {...p}><path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-1 1.5-2 1.5-3.5 0-3-2.5-5-5-5s-5 2-5 5c0 1.5.5 2.5 1.5 3.5.8.8 1.3 1.5 1.5 2.5" /><path d="M9 18h6" /><path d="M10 22h4" /></IconWrapper>;
const Wrench = (p) => <IconWrapper {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></IconWrapper>;
const CheckSquare = (p) => <IconWrapper {...p}><path d="m9 11 3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></IconWrapper>;
const Quote = (p) => <IconWrapper {...p}><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1z" /></IconWrapper>;
const Rocket = (p) => <IconWrapper {...p}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></IconWrapper>;
const ClipboardCheck = (p) => <IconWrapper {...p}><rect width="8" height="4" x="8" y="2" rx="1" ry="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><path d="m9 14 2 2 4-4" /></IconWrapper>;

// --- Global Styles for Animations & Fonts ---
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap');

    html {
      scroll-behavior: smooth;
    }

    body {
      font-family: 'Montserrat', sans-serif;
      background-color: #020617; /* Slate 950 */
      overflow-x: hidden;
    }

    /* --- Cinematic Slow Animations --- */
    @keyframes slowFloat {
      0%, 100% { transform: translateY(0px) scale(1); }
      50% { transform: translateY(-20px) scale(1.05); }
    }
    .animate-slow-float {
      animation: slowFloat 8s ease-in-out infinite;
    }
    .animate-slow-float-delayed {
      animation: slowFloat 10s ease-in-out infinite;
      animation-delay: 2s;
    }

    /* --- Infinite Scroll Animation --- */
    @keyframes scroll {
      0% { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .animate-scroll {
      animation: scroll 30s linear infinite;
    }
    .pause-on-hover:hover {
      animation-play-state: paused;
    }

    /* --- Glassmorphism Utilities --- */
    .glass-panel {
      background: rgba(15, 23, 42, 0.3); /* Slate-900 with low opacity */
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.05);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    }
    
    .glass-card {
      background: linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.08);
      box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
    }
    
    .glass-input {
      background: rgba(2, 6, 23, 0.5);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: #e2e8f0;
    }
    .glass-input:focus {
      border-color: #25789a;
      box-shadow: 0 0 15px rgba(37, 120, 154, 0.2);
    }

    /* --- Icon Animations --- */
    
    /* 1. Draw Path */
    .icon-draw-path {
      stroke-dasharray: 100;
      stroke-dashoffset: 100;
      transition: stroke-dashoffset 1s ease-in-out;
    }
    .group:hover .icon-draw-path {
      stroke-dashoffset: 0;
    }

      /* 2. Redraw */
    .icon-redraw {
      stroke-dasharray: 100;
      stroke-dashoffset: 100; /* Hidden by default for ALL devices to support load animation */
      transition: stroke-dashoffset 1s ease-in-out; 
    }

    @keyframes draw-stroke {
      0% { stroke-dashoffset: 100; }
      100% { stroke-dashoffset: 0; }
    }

    /* 3. Pop In (Zoom effect) */
    @keyframes icon-pop-anim {
      0% { transform: scale(0); opacity: 0; }
      100% { transform: scale(1); opacity: 1; }
    }

    .icon-pop-trigger {
      transform: scale(0);
      opacity: 0;
      transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    }

    /* 4. Clock Spin */
    @keyframes clock-spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes clock-spin-fast {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(720deg); }
    }

    .icon-clock-hand-1 {
      transform-origin: center;
      transform: rotate(0deg);
      transition: transform 1s ease-out;
    }
    .icon-clock-hand-2 {
      transform-origin: center;
      transform: rotate(0deg);
      transition: transform 1.5s ease-out;
    }
    
    /* --- Load-In Triggers (All Devices) --- */
    .in-view .icon-redraw, .carousel-active .icon-redraw {
      stroke-dashoffset: 0; 
      /* Delay controlled by inline styles in components */
    }

    .in-view .icon-pop-trigger, .carousel-active .icon-pop-trigger {
      transform: scale(1);
      opacity: 1;
    }

    .in-view .icon-clock-hand-1, .carousel-active .icon-clock-hand-1 {
      transform: rotate(360deg);
    }
    .in-view .icon-clock-hand-2, .carousel-active .icon-clock-hand-2 {
      transform: rotate(720deg);
    }


    /* Desktop Hover Trigger overrides */
    @media (min-width: 1025px) {
      .group:hover .icon-redraw {
        animation: draw-stroke 1.5s ease-in-out both;
        /* Animation overrides transition, so the load delay won't affect hover */
      }
      
      .group:hover .icon-pop-trigger {
          animation: icon-pop-anim 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
      }

      .group:hover .icon-clock-hand-1 {
        animation: clock-spin 1s ease-out both;
      }
      .group:hover .icon-clock-hand-2 {
        animation: clock-spin-fast 1.5s ease-out both;
      }
    }

    /* --- Search Highlight --- */
    .highlight {
      background-color: rgba(37, 120, 154, 0.3);
      color: #4ab3d5;
      font-weight: 700;
      padding: 0 2px;
      border-radius: 3px;
    }

    /* --- Grid Expansion Animation Classes --- */
    .rows-expand {
        grid-template-rows: 1fr;
    }
    .rows-collapse {
        grid-template-rows: 0fr;
    }
    .transition-rows {
        transition: grid-template-rows 0.5s ease-in-out;
    }
    
    /* Custom Scrollbar for card content if needed */
    .custom-scrollbar::-webkit-scrollbar {
        width: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
        background: rgba(15, 23, 42, 0.5);
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #25789a;
        border-radius: 2px;
    }
  `}</style>
);

// --- Utility Components ---

const ScrollLink = ({ to, children, className, onClick }) => {
  const handleClick = (e) => {
    e.preventDefault(); // Always prevent the default instant jump
    
    if (to === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const element = document.getElementById(to);
      if (element) {
        // Use scrollIntoView with smooth behavior for consistent animation
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    
    if (onClick) onClick();
  };
  return (
    <a href={to === 'top' ? '#' : `#${to}`} onClick={handleClick} className={className}>
      {children}
    </a>
  );
};

const useOnScreen = (options) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect(); 
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) observer.unobserve(ref.current);
    };
  }, [ref, options]);

  return [ref, isVisible];
};

const FadeInSection = ({ children, delay = 0, className = "", threshold = 0.1 }) => {
  const [ref, isVisible] = useOnScreen({ threshold });
  
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      // Added 'in-view' class if visible for CSS targeting
      className={`transition-all duration-1000 ease-out transform ${
        isVisible ? 'opacity-100 translate-y-0 in-view' : 'opacity-0 translate-y-10'
      } ${className}`}
    >
      {children}
    </div>
  );
};

const HighlightText = ({ text, highlight }) => {
  if (!highlight.trim()) {
    return <span>{text}</span>;
  }
  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);
  return (
    <span>
      {parts.map((part, i) => 
        regex.test(part) ? <span key={i} className="highlight">{part}</span> : part
      )}
    </span>
  );
};

const AbstractBackground = () => {
  return (
    <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none bg-[#020617]">
      {/* Deep dark gradient base */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-[#020617] to-black" />
      
      {/* Subtle Grid Perspective */}
      <div className="absolute inset-0 opacity-20" 
           style={{ 
             backgroundImage: `linear-gradient(#25789a 1px, transparent 1px), linear-gradient(90deg, #25789a 1px, transparent 1px)`, 
             backgroundSize: '4rem 4rem',
             backgroundPosition: 'center top',
             transform: 'perspective(500px) rotateX(60deg) translateY(-100px) scale(2)',
             maskImage: 'linear-gradient(to bottom, transparent 0%, black 40%, black 100%)'
           }} 
      />

      {/* Cinematic Slow Orbs - Less chaotic */}
      <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] bg-[#25789a] rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-slow-float" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[35rem] h-[35rem] bg-indigo-900 rounded-full mix-blend-screen filter blur-[128px] opacity-20 animate-slow-float-delayed" />
    </div>
  );
};

// --- Custom Animated Icons for Benefits Section ---

const AnimatedClock = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="6" x2="12" y2="12" className="icon-clock-hand-1" style={{ transitionDelay: '0.2s', animationDelay: '0.2s' }} />
    <line x1="16" y1="12" x2="12" y2="12" className="icon-clock-hand-2" style={{ transitionDelay: '0.2s', animationDelay: '0.2s' }} />
  </svg>
);

const AnimatedTrend = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 18 8.5 10.5 13.5 15.5 23 6" className="icon-redraw" style={{ transitionDelay: '0.2s', animationDelay: '0.2s' }} />
    <polyline points="17 6 23 6 23 12" className="icon-redraw" style={{ transitionDelay: '1.0s', animationDelay: '1.0s' }} />
  </svg>
);

const AnimatedPulse = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12h4l3 9 6-18 3 9h4" className="icon-redraw" style={{ transitionDelay: '0.2s', animationDelay: '0.2s' }} />
  </svg>
);

const AnimatedMessage = () => (
  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <line x1="8" y1="9" x2="16" y2="9" className="icon-pop-trigger" style={{ transitionDelay: '0.3s', animationDelay: '0.3s' }} />
    <line x1="8" y1="13" x2="14" y2="13" className="icon-pop-trigger" style={{ transitionDelay: '0.5s', animationDelay: '0.5s' }} />
  </svg>
);

// --- Privacy Orb 3D Component (Enhanced for Organic "Brain-like" Effect) ---

const privacyOrbVertexShader = `
  uniform float uTime;
  uniform float uDistortion;
  uniform float uSpeed;
  
  varying float vIntensity;
  varying vec3 vNormal;

  // Ashima Simplex Noise 3D
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

  float snoise(vec3 v) { 
    const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
    const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

    // First corner
    vec3 i  = floor(v + dot(v, C.yyy) );
    vec3 x0 = v - i + dot(i, C.xxx) ;

    // Other corners
    vec3 g = step(x0.yzx, x0.xyz);
    vec3 l = 1.0 - g;
    vec3 i1 = min( g.xyz, l.zxy );
    vec3 i2 = max( g.xyz, l.zxy );

    //  x0 = x0 - 0.0 + 0.0 * C 
    vec3 x1 = x0 - i1 + 1.0 * C.xxx;
    vec3 x2 = x0 - i2 + 2.0 * C.xxx;
    vec3 x3 = x0 - 1.0 + 3.0 * C.xxx;

    // Permutations
    i = mod289(i); 
    vec4 p = permute( permute( permute( 
              i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
            + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
            + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

    // Gradients
    float n_ = 1.0/7.0; // N=7
    vec3  ns = n_ * D.wyz - D.xzx;

    vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

    vec4 x_ = floor(j * ns.z);
    vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

    vec4 x = x_ *ns.x + ns.yyyy;
    vec4 y = y_ *ns.x + ns.yyyy;
    vec4 h = 1.0 - abs(x) - abs(y);

    vec4 b0 = vec4( x.xy, y.xy );
    vec4 b1 = vec4( x.zw, y.zw );

    vec4 s0 = floor(b0)*2.0 + 1.0;
    vec4 s1 = floor(b1)*2.0 + 1.0;
    vec4 sh = -step(h, vec4(0.0));

    vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
    vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

    vec3 p0 = vec3(a0.xy,h.x);
    vec3 p1 = vec3(a0.zw,h.y);
    vec3 p2 = vec3(a1.xy,h.z);
    vec3 p3 = vec3(a1.zw,h.w);

    //Normalise gradients
    vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
    p0 *= norm.x;
    p1 *= norm.y;
    p2 *= norm.z;
    p3 *= norm.w;

    // Mix final noise value
    vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
    m = m * m;
    return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                dot(p2,x2), dot(p3,x3) ) );
  }

  // Fractional Brownian Motion (fBm) for organic cellular detail
  float fbm(vec3 x) {
    float v = 0.0;
    float a = 0.5;
    vec3 shift = vec3(100.0);
    // Stack 3 layers of noise for the "brain-like" texture
    for (int i = 0; i < 3; ++i) {
        v += a * snoise(x);
        x = x * 2.0 + shift;
        a *= 0.5;
    }
    return v;
  }

  void main() {
    vNormal = normalize(normalMatrix * normal);
    
    // Organic Displacement using fBm
    // Reduced frequency (1.0) for smoother, curvier waves
    float noiseVal = fbm(position * 1.0 + uTime * uSpeed); 
    
    // Displace vertices along their normal vector
    vec3 newPos = position + normal * noiseVal * uDistortion;
    
    vec4 mvPosition = modelViewMatrix * vec4(newPos, 1.0);
    gl_Position = projectionMatrix * mvPosition;
    
    // Fresnel / Rim Lighting calculation
    vec3 viewDir = normalize(-mvPosition.xyz);
    float fresnel = pow(1.0 - dot(vNormal, viewDir), 2.0); 
    vIntensity = fresnel;
  }
`;

const privacyOrbFragmentShader = `
  uniform vec3 uColor;
  varying float vIntensity;

  void main() {
    // NET EFFECT: Continuous lines, no discard logic
    
    // Combine base color with Fresnel intensity
    // Lowered brightness multiplier for subtlety
    float brightness = 0.5 + vIntensity * 1.0; 
    vec3 finalColor = uColor * brightness;
    
    // Opacity driven by Fresnel - much lower base opacity (0.05) for subtle background feel
    gl_FragColor = vec4(finalColor, 0.05 + vIntensity * 0.3); 
  }
`;

const PrivacyOrb = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Scene Setup
    const scene = new THREE.Scene();
    
    // Camera - FOV 35 for "heroic" look as per blueprint
    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    camera.position.z = 4.5; // Adjust distance to fit container

    // Renderer
    const renderer = new THREE.WebGLRenderer({ 
      antialias: true, 
      alpha: true,
      powerPreference: "high-performance" 
    });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    containerRef.current.appendChild(renderer.domElement);

    // Geometry - Detail 4 for smoother, rounder curves (Detail 3 was angular)
    const geometry = new THREE.IcosahedronGeometry(1, 4); 

    // Material - Shader Material for Mesh (Wireframe)
    const material = new THREE.ShaderMaterial({
      vertexShader: privacyOrbVertexShader,
      fragmentShader: privacyOrbFragmentShader,
      uniforms: {
        uTime: { value: 0.0 },
        uColor: { value: new THREE.Color('#4ab3d5') }, // Light cyan color
        uDistortion: { value: 0.25 }, // Gentle distortion
        uSpeed: { value: 0.2 } // Slow breathing
      },
      wireframe: true, // Connected net
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });

    // Mesh (Connected Net)
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // Group for Rotation Interaction
    const group = new THREE.Group();
    group.add(mesh);
    scene.add(group);

    // Interaction State
    const mouse = new THREE.Vector2();
    const targetRotation = new THREE.Vector2();
    
    const handleMouseMove = (event) => {
      // Normalize mouse (-1 to 1)
      const x = (event.clientX / window.innerWidth) * 2 - 1;
      const y = -(event.clientY / window.innerHeight) * 2 + 1;
      
      // Target rotation (tilt amount)
      // FIX 3: Reverted Y axis to positive (y) so vertical rotation aligns with mouse movement preference
      targetRotation.set(y * 0.5, -x * 0.5);
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Resize Handler
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);

    // Animation Loop
    const clock = new THREE.Clock();
    let frameId;

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const elapsedTime = clock.getElapsedTime();

      // Update Uniforms
      material.uniforms.uTime.value = elapsedTime;

      // Idle Animation (Weightlessness + Rotation)
      mesh.rotation.y += 0.0015; // Slow constant rotation
      group.position.y = Math.sin(elapsedTime * 0.5) * 0.1; // Gentle float

      // Mouse Interaction (Lagged Follow/Lerp for "buttery smooth" motion)
      // Lerp factor 0.05 gives it a nice weight
      group.rotation.x += (targetRotation.x - group.rotation.x) * 0.05;
      group.rotation.y += (targetRotation.y - group.rotation.y) * 0.05;

      renderer.render(scene, camera);
    };

    animate();

    // Cleanup
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameId);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      geometry.dispose();
      material.dispose();
      renderer.dispose();
    };
  }, []);

  return <div ref={containerRef} className="w-full h-full" />;
};

// --- Navbar Component (Restored) ---
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navClasses = `fixed w-full z-50 transition-all duration-500 ${
    scrolled 
      ? 'bg-[#020617]/80 backdrop-blur-xl border-b border-white/5 py-4 shadow-lg' 
      : 'bg-transparent py-6'
  }`;

  const linkClasses = "text-slate-300 hover:text-white transition-colors font-medium text-sm tracking-wide";

  return (
    <nav className={navClasses}>
      <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
        <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-[#25789a]/50 flex items-center justify-center shadow-[0_0_15px_rgba(37,120,154,0.2)] group-hover:shadow-[0_0_25px_rgba(37,120,154,0.4)] transition-all duration-300">
            <span className="text-[#25789a] font-extrabold text-xl group-hover:scale-110 transition-transform">Q</span>
          </div>
          <span className="text-white font-bold text-xl tracking-tight">
            quantM <span className="text-[#25789a]">Solutions AI</span>
          </span>
        </div>

        <div className="hidden md:flex gap-8 items-center bg-slate-900/30 backdrop-blur-md px-8 py-3 rounded-full border border-white/5 shadow-inner">
          <ScrollLink to="services" className={linkClasses}>Services</ScrollLink>
          <ScrollLink to="benefits" className={linkClasses}>Benefits</ScrollLink>
          <ScrollLink to="process" className={linkClasses}>Process</ScrollLink>
          <ScrollLink to="audit" className="text-[#25789a] hover:text-white transition-colors font-bold cursor-pointer tracking-wide flex items-center gap-1">
             <Sparkles size={14} /> Audit
          </ScrollLink>
          <ScrollLink to="pricing" className={linkClasses}>Pricing</ScrollLink>
          <ScrollLink to="privacy" className={linkClasses}>Privacy</ScrollLink>
          <ScrollLink to="about" className={linkClasses}>About</ScrollLink>
          <ScrollLink to="faq" className={linkClasses}>FAQ</ScrollLink>
        </div>

        <ScrollLink 
            to="contact" 
            className="hidden md:block px-6 py-2.5 rounded-full bg-[#25789a]/10 border border-[#25789a]/50 text-[#25789a] font-semibold hover:bg-[#25789a] hover:text-white transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,120,154,0.4)] text-sm"
        >
            Contact
        </ScrollLink>

        <button className="md:hidden text-white p-2" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <X /> : <Menu />}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden absolute top-full left-0 w-full bg-[#020617]/95 backdrop-blur-xl border-b border-white/10 p-6 flex flex-col gap-6 shadow-2xl animate-in slide-in-from-top-5">
          <ScrollLink to="services" onClick={() => setIsOpen(false)} className={linkClasses}>Services</ScrollLink>
          <ScrollLink to="benefits" onClick={() => setIsOpen(false)} className={linkClasses}>Benefits</ScrollLink>
          <ScrollLink to="process" onClick={() => setIsOpen(false)} className={linkClasses}>Process</ScrollLink>
          <ScrollLink to="audit" onClick={() => setIsOpen(false)} className={linkClasses}>AI Audit</ScrollLink>
          <ScrollLink to="pricing" onClick={() => setIsOpen(false)} className={linkClasses}>Pricing</ScrollLink>
          <ScrollLink to="privacy" onClick={() => setIsOpen(false)} className={linkClasses}>Privacy</ScrollLink>
          <ScrollLink to="about" onClick={() => setIsOpen(false)} className={linkClasses}>About</ScrollLink>
          <ScrollLink to="faq" onClick={() => setIsOpen(false)} className={linkClasses}>FAQ</ScrollLink>
          <ScrollLink to="contact" onClick={() => setIsOpen(false)} className="text-[#25789a] font-bold text-center py-3 border border-[#25789a] rounded-xl">Contact Us</ScrollLink>
        </div>
      )}
    </nav>
  );
};

// --- Hero Component (Restored) ---
const Hero = () => {
  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-20 px-6 z-10 scroll-mt-28 overflow-hidden">
      
      {/* 🔮 Heroic Privacy Orb Effect - Centered Background */}
      {/* Reduced opacity to 20% for subtlety */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[900px] h-[600px] md:h-[900px] z-0 opacity-20 pointer-events-none">
        <PrivacyOrb />
      </div>

      <div className="max-w-5xl mx-auto text-center relative z-20">
        <FadeInSection>
          <div className="inline-block mb-6 px-4 py-1.5 rounded-full border border-[#25789a]/30 bg-[#25789a]/10 text-[#25789a] text-xs font-bold tracking-widest uppercase shadow-[0_0_15px_rgba(37,120,154,0.1)]">
            Built for Growth
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-extrabold text-white mb-8 leading-[1.1] tracking-tight">
            Scale Your Business with <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#25789a] to-cyan-400">Custom AI Automation</span>
          </h1>
          
          <div className="flex flex-col items-center justify-center mb-12 space-y-4 animate-in slide-in-from-bottom-4 duration-1000 delay-300 fade-in">
            <div className="flex items-center gap-3 md:gap-6 text-xl md:text-3xl font-medium text-slate-300">
               <span className="relative group cursor-default transition-colors hover:text-white">
                 Plan
                 <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-slate-800 rounded-full group-hover:bg-slate-600 transition-colors"></span>
               </span>
               <ArrowRight size={24} className="text-[#25789a] animate-pulse" />
               <span className="relative group cursor-default transition-colors hover:text-white">
                 Build
                 <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-slate-800 rounded-full group-hover:bg-slate-600 transition-colors"></span>
               </span>
               <ArrowRight size={24} className="text-[#25789a] animate-pulse" style={{ animationDelay: '0.5s' }} />
               <span className="relative text-white font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-200">
                 Deploy
                 <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-gradient-to-r from-[#25789a] to-cyan-400 rounded-full shadow-[0_0_10px_#25789a]"></span>
               </span>
            </div>
            <p className="text-slate-400 font-light text-base md:text-lg tracking-wide mt-2">
               Eliminate bottlenecks and unlock capacity. Systems ready in <span className="text-[#25789a] font-semibold border-b border-[#25789a]/30 pb-0.5">weeks</span>.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <ScrollLink 
                to="contact"
                className="w-full sm:w-auto px-8 py-4 rounded-full bg-gradient-to-r from-[#25789a] to-cyan-600 text-white font-bold text-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(37,120,154,0.5)] hover:scale-105"
            >
                Get Started Now
            </ScrollLink>
            <ScrollLink 
                to="audit"
                className="w-full sm:w-auto px-8 py-4 rounded-full glass-panel text-white font-medium text-lg transition-all duration-300 hover:bg-white/10 hover:border-white/20 flex items-center justify-center gap-2"
            >
                <Sparkles size={20} className="text-[#25789a]" />
                Try AI Audit
            </ScrollLink>
          </div>
        </FadeInSection>
      </div>
    </section>
  );
};

// --- Service Modal Component ---
const ServiceModal = ({ service, onClose }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  // Lock Scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  if (!service) return null;

  // Pricing Calculation Logic
  const calculatePrice = () => {
    if (!service.pricing) return null;
    
    const { otp, monthly } = service.pricing;
    
    if (isAnnual) {
        // Annual: 50% off OTP, ~17% off Monthly (2 months free)
        return {
            otp: Math.round(otp * 0.5),
            monthly: Math.round((monthly * 10) / 12)
        };
    }
    return { otp, monthly };
  };

  const prices = calculatePrice();

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      {/* Modal Content - Made Wider (max-w-5xl) & Taller (max-h-[90vh]) */}
      <div className="relative w-full max-w-5xl bg-[#0f172a] border border-[#25789a] rounded-[24px] shadow-[0_0_50px_rgba(37,120,154,0.3)] overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-300">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 text-white hover:bg-[#25789a] transition-colors"
        >
          <X size={20} />
        </button>

        {/* Video / Hero Section - Fixed height instead of aspect-ratio to save vertical space */}
        <div className="w-full h-56 md:h-72 bg-slate-900 relative group cursor-pointer border-b border-slate-800 shrink-0">
             {/* Simple Placeholder for Video */}
             <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
                <div className="w-16 h-16 rounded-full bg-[#25789a]/20 border border-[#25789a] flex items-center justify-center group-hover:scale-110 transition-transform shadow-[0_0_20px_rgba(37,120,154,0.4)]">
                   <div className="w-0 h-0 border-t-[10px] border-t-transparent border-l-[18px] border-l-white border-b-[10px] border-b-transparent ml-1" />
                </div>
             </div>
             <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#0f172a] to-transparent pointer-events-none" />
             <div className="absolute bottom-6 left-6 z-10">
                <div className="flex items-center gap-2 text-[#25789a] text-xs font-bold uppercase tracking-wider mb-2">
                   <div className="p-1 bg-[#25789a]/20 rounded">{service.icon}</div> 
                   <span>System Demo</span>
                </div>
                <h3 className="text-2xl md:text-4xl font-extrabold text-white">{service.title}</h3>
             </div>
        </div>

        {/* Scrollable Body */}
        <div className="p-6 md:p-8 overflow-y-auto custom-scrollbar flex-1">
           <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
              <div className="flex-1">
                 <h4 className="text-xl font-bold text-white mb-4">About this Solution</h4>
                 <p className="text-slate-300 font-light text-base leading-relaxed mb-8">
                    {service.detailedDesc || service.desc}
                 </p>
                 
                 <h4 className="text-sm font-bold text-white uppercase tracking-wider mb-4">Key Features</h4>
                 <ul className="space-y-3">
                    {service.list.map((item, i) => (
                       <li key={i} className="flex items-start gap-3 text-base text-slate-400">
                          <span className="text-[#25789a] mt-1 font-bold">✓</span> 
                          <span className="leading-snug">{item}</span>
                       </li>
                    ))}
                 </ul>
              </div>
              
              {/* Pricing / CTA Box - Fixed width on desktop */}
              <div className="w-full md:w-80 flex-shrink-0">
                  <div className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 sticky top-0">
                      
                      {prices ? (
                        <>
                            {/* Toggle */}
                            <div className="flex items-center justify-between mb-6 bg-slate-950 rounded-lg p-1 border border-slate-800">
                                <button 
                                    onClick={() => setIsAnnual(false)}
                                    className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${!isAnnual ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                >
                                    Monthly
                                </button>
                                <button 
                                    onClick={() => setIsAnnual(true)}
                                    className={`flex-1 py-2 text-xs font-bold rounded-md transition-all ${isAnnual ? 'bg-[#25789a] text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                                >
                                    Annual
                                </button>
                            </div>

                            <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-2">Estimated Investment</div>
                            
                            <div className="mb-2">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-3xl font-extrabold text-white">£{prices.otp}</span>
                                    <span className="text-sm font-bold text-[#25789a] uppercase">OTP Setup</span>
                                </div>
                                {isAnnual && <div className="text-xs text-green-400 font-medium mt-1">50% Setup Discount Applied</div>}
                            </div>

                            <div className="mb-6 pt-4 border-t border-slate-800/50">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-2xl font-bold text-white">+ £{prices.monthly}</span>
                                    <span className="text-sm font-medium text-slate-400">/ mo</span>
                                </div>
                                <div className="text-xs text-slate-500 italic mt-1">Maintenance & Support</div>
                                {isAnnual && <div className="text-xs text-green-400 font-medium mt-1">2 Months Free Included</div>}
                            </div>
                        </>
                      ) : (
                        <div className="mb-6">
                            <div className="text-xs text-slate-500 font-bold uppercase tracking-wider mb-1">Estimated Investment</div>
                            <div className="text-3xl font-extrabold text-white">Custom Quote</div>
                        </div>
                      )}
                      
                      <ScrollLink 
                        to="contact" 
                        onClick={onClose}
                        className="block w-full py-4 bg-[#25789a] text-white text-center font-bold rounded-xl hover:bg-[#1e6280] transition-all duration-300 hover:shadow-[0_0_20px_rgba(37,120,154,0.4)] text-sm mb-3"
                      >
                        Book Consultation
                      </ScrollLink>
                      <button 
                          onClick={onClose}
                          className="block w-full py-3 bg-transparent border border-slate-700 text-slate-400 text-center font-semibold rounded-xl hover:border-slate-500 hover:text-white transition-colors text-sm"
                      >
                          Close
                      </button>
                  </div>
              </div>
           </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

const Services = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  const servicesData = [
    {
      icon: <Bot size={40} />,
      title: "AI Chatbots & Voice Agents",
      desc: "Intelligent agents that handle inquiries, bookings, and support via text or voice, 24/7.",
      detailedDesc: "Our AI agents are more than just chatbots. They understand context, handle complex booking flows, and can even process payments directly within the chat. Whether it's a voice receptionist for your clinic or a support agent for your SaaS, we build systems that feel human.",
      pricing: { otp: 299, monthly: 199 },
      list: [
        "Multi-channel: Website, WhatsApp, Email & Voice Calls",
        "Voice Receptionist: Answers calls, confirms bookings & takes queries",
        "Smart Actions: Collects data & books meetings via live calendar sync",
        "RAG Powered: Answers FAQs using your business data"
      ],
      extra: "Data processed securely, never stored longer than needed."
    },
    {
      icon: <FileText size={40} />,
      title: "Invoice Manager",
      desc: "Automate how invoices and business documents are processed and organised.",
      detailedDesc: "Stop manual data entry. Our system automatically watches your inbox for invoices, extracts line-item data using computer vision, validates it against your POs, and syncs it directly to Xero or QuickBooks.",
      pricing: { otp: 199, monthly: 150 },
      list: [
        "Extracts key data to Sheets/CRMs",
        "Smart Filing: Sorts into vendor folders & renames (Date-Vendor-Desc)",
        "Sends confirmation alert emails",
        "Syncs with QuickBooks, Xero, Airtable"
      ]
    },
    {
      icon: <Funnel size={40} />,
      title: "Lead Generation",
      desc: "AI system that captures, enriches, and scores leads automatically.",
      detailedDesc: "Turn cold traffic into qualified prospects. We scrape targeted data sources, enrich profiles with LinkedIn and company data, and use LLMs to score leads based on your ideal customer profile before they even hit your CRM.",
      pricing: { otp: 249, monthly: 179 },
      list: [
        "Collects leads from forms/chatbots",
        "Enriches data (Maps, LinkedIn, Hunter)",
        "Syncs high-quality leads to CRM",
        "Seamlessly extends into automated cold outreach workflows"
      ]
    },
    {
      icon: <Mail size={40} />,
      title: "Cold Outreach",
      desc: "AI-powered outreach that personalises and automates client communication.",
      detailedDesc: "Scale your outreach without losing the personal touch. Our system researches each prospect and generates unique, hyper-personalised emails that get opened. It handles follow-ups automatically until a reply is detected.",
      pricing: { otp: 229, monthly: 169 },
      list: [
        "Generates personalised one-to-one emails",
        "Mass Marketing: Scalable campaigns for broader audiences",
        "Tracks engagement & triggers follow-ups",
        "Integrates with Gmail/Outlook/CRMs"
      ]
    },
    {
      icon: <Cpu size={40} />,
      title: "Custom System Build",
      desc: "Tailored AI automation built around your unique business needs.",
      detailedDesc: "Have a unique bottleneck? We build bespoke Python and n8n pipelines to solve specific operational challenges. From inventory management to automated reporting, if it repeats, we can automate it.",
      priceRange: "Custom Quote",
      list: [
        "Complex workflows (Chatbots + CRM + API)",
        "n8n & Python pipelines",
        "Enhanced privacy via self-hosted/local solutions",
        "Integrates Google, Microsoft, OpenAI, WhatsApp"
      ]
    }
  ];

  const filteredServices = servicesData.filter(service => {
    const searchLower = searchTerm.toLowerCase();
    return (
      service.title.toLowerCase().includes(searchLower) ||
      service.desc.toLowerCase().includes(searchLower) ||
      service.list.some(item => item.toLowerCase().includes(searchLower))
    );
  });

  const handleCardClick = (index) => {
    if (window.innerWidth <= 1024) {
        setExpandedIndex(prev => prev === index ? null : index);
    }
  };

  return (
    <section id="services" className="relative py-24 px-6 z-10 scroll-mt-28">
      {selectedService && (
         <ServiceModal service={selectedService} onClose={() => setSelectedService(null)} />
      )}

      <div className="max-w-7xl mx-auto">
        <FadeInSection>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
              Our <span className="text-[#25789a] animate-color-pulse">Services</span>
            </h2>
            <p className="text-lg text-slate-400 font-light max-w-2xl mx-auto">
              Streamline your business with AI-powered automation solutions.
            </p>
          </div>

          <div className="flex justify-center mb-16">
            <div className="relative w-full max-w-[600px] group">
              <input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-12 py-3 bg-slate-900/40 backdrop-blur-sm border border-[#25789a] rounded-[30px] text-[#FEFFFE] font-medium focus:outline-none focus:border-[#4ab3d5] focus:shadow-[0_0_15px_rgba(37,120,154,0.3)] transition-all duration-300 placeholder-slate-500"
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#25789a]" size={20} />
              
              {searchTerm && (
                <button 
                  onClick={() => setSearchTerm('')}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-[#25789a] transition-colors"
                >
                  <XCircle size={20} />
                </button>
              )}
            </div>
          </div>
        </FadeInSection>
        
        {filteredServices.length > 0 ? (
          /* Changed from 3 columns to 2 columns on large screens to make cards wider */
          <div className="grid md:grid-cols-2 gap-8">
            {filteredServices.map((service, index) => (
              <FadeInSection key={index} delay={index * 100} className="h-full">
                
                <div 
                    className={`group relative w-full h-auto bg-slate-900/40 backdrop-blur-md border border-slate-800 rounded-[20px] p-6 transition-all duration-300 hover:border-[#25789a] hover:shadow-[0_0_20px_rgba(37,120,154,0.15)] cursor-pointer overflow-hidden ${expandedIndex === index ? 'border-[#25789a] shadow-[0_0_20px_rgba(37,120,154,0.15)]' : ''}`}
                    onClick={() => handleCardClick(index)}
                > 
                    {/* Header Section */}
                    <div className="flex flex-col items-center relative z-10">
                        <div className="mb-3 p-3 rounded-full bg-slate-800 text-[#25789a] group-hover:bg-[#25789a] group-hover:text-white transition-colors duration-300 shadow-[0_0_10px_rgba(37,120,154,0.3)]">
                            {service.icon}
                        </div>
                        <h3 className="text-lg font-bold text-white mb-2 text-center">
                            <HighlightText text={service.title} highlight={searchTerm} />
                        </h3>
                        <p className="text-center text-slate-300 mb-2 font-light text-sm leading-snug">
                            <HighlightText text={service.desc} highlight={searchTerm} />
                        </p>
                        
                        {/* Prompt Text - Fades out on Hover/Expand */}
                        <div className={`mt-2 transition-opacity duration-300 overflow-hidden ${expandedIndex === index ? 'opacity-0 delay-0' : 'opacity-100 delay-500'} group-hover:opacity-0 group-hover:delay-0`}>
                            <p className="text-[10px] font-bold text-[#25789a] tracking-widest uppercase animate-pulse">
                                <span className="hidden lg:inline">Hover for Details</span>
                                <span className="lg:hidden">Tap for Details</span>
                            </p>
                        </div>
                    </div>

                    {/* Expandable Details Section */}
                    <div 
                      className={`grid transition-[grid-template-rows] duration-500 ease-in-out 
                      ${expandedIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'} 
                      lg:group-hover:grid-rows-[1fr]`}
                    >
                        <div className="overflow-hidden">
                            <div className="pt-4 mt-2 border-t border-slate-700/50 flex flex-col items-center">
                                <ul className="w-full list-none p-0 text-slate-300 text-sm space-y-2 text-left leading-snug mb-4">
                                    {service.list.map((item, i) => (
                                        <li key={i} className="relative pl-5">
                                            <span className="absolute left-0 top-[2px] text-[#25789a] font-bold">•</span>
                                            <HighlightText text={item} highlight={searchTerm} />
                                        </li>
                                    ))}
                                </ul>
                                
                                <button 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedService(service);
                                    }}
                                    className="w-full py-2.5 rounded-lg bg-[#25789a]/10 border border-[#25789a]/50 text-[#25789a] font-bold text-xs uppercase tracking-wider hover:bg-[#25789a] hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group-hover:shadow-[0_0_15px_rgba(37,120,154,0.4)]"
                                >
                                    <Sparkles size={14} /> View Demo & Pricing
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

              </FadeInSection>
            ))}
          </div>
        ) : (
          <FadeInSection>
            <div className="text-center py-12">
              <p className="text-xl text-slate-400">No services found matching "{searchTerm}".</p>
              <button 
                onClick={() => setSearchTerm('')} 
                className="mt-4 text-[#25789a] hover:underline"
              >
                Clear Search
              </button>
            </div>
          </FadeInSection>
        )}

        <FadeInSection delay={300}>
            <div className="mt-16 mx-auto max-w-3xl text-center">
                <h2 className="text-2xl font-bold text-white mb-6">Ready to Transform Your Business?</h2>
                <ScrollLink 
                    to="contact" 
                    className="inline-flex items-center gap-2 px-8 py-4 rounded-[30px] bg-[#25789a] text-white font-bold text-lg transition-all duration-300 hover:bg-transparent border border-transparent hover:border-[#25789a] hover:shadow-[0_0_20px_#25789a] hover:scale-105"
                >
                    💡 Book a Demo – See It in Action
                </ScrollLink>
            </div>
        </FadeInSection>
      </div>
    </section>
  );
};

const AIAudit = () => {
  const [industry, setIndustry] = useState('');
  const [bottleneck, setBottleneck] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const generateAudit = async (e) => {
    e.preventDefault();
    if (!industry || !bottleneck) return;
    
    setLoading(true);
    setError('');
    setResults(null);

    const prompt = `
      Act as a senior automation engineer at quantM Solutions AI.
      User Industry: ${industry}
      User Pain Point: ${bottleneck}
      
      Task: Suggest 3 concrete, high-impact automation solutions.
      Constraints:
      - Use British English spelling (e.g. 'optimise', 'personalised', 'programme').
      - STRICT TOOL STACK: n8n, Python, Google Workspace, Microsoft 365, OpenAI (and other LLMs), CRMs, APIs, LinkedIn, Accounting Software, WhatsApp.
      - DO NOT mention Zapier or Make.
      - Keep it concise.
      - Return ONLY a valid JSON array of objects. Do not include any markdown formatting like \`\`\`json or \`\`\`.
      - Schema: [{"tool": "Tool Name", "strategy": "Strategy Description", "benefit": "Benefit Description"}]
    `;

    try {
      const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${apiKey}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }]
        })
      });

      const data = await response.json();
      if (data.candidates && data.candidates[0].content) {
        let rawText = data.candidates[0].content.parts[0].text;
        rawText = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
        
        try {
            const parsedResults = JSON.parse(rawText);
            setResults(parsedResults);
        } catch (parseError) {
            console.error("JSON Parse Error:", parseError);
            setError("Could not format the strategy. Please try again.");
        }
      } else {
        setError("Could not generate strategies. Please try again.");
      }
    } catch (err) {
      setError("Something went wrong. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="audit" className="relative py-20 px-6 z-10 scroll-mt-28">
      <div className="max-w-6xl mx-auto">
        <FadeInSection>
          <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-[#25789a] rounded-[20px] p-8 md:p-12 shadow-[0_0_30px_rgba(37,120,154,0.15)] relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#25789a] rounded-full mix-blend-screen filter blur-[100px] opacity-10 pointer-events-none" />

            <div className="relative z-10 text-center mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-1 rounded-full bg-[#25789a]/20 text-[#25789a] font-bold text-sm mb-4 border border-[#25789a]/30">
                <Sparkles size={16} />
                <span>Powered by Gemini 2.5</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-4">
                ✨ Instant Automation <span className="text-[#25789a]">Audit</span>
              </h2>
              <p className="text-slate-400 font-light max-w-2xl mx-auto">
                Tell us your industry and biggest bottleneck. Our AI will instantly generate 3 tailored automation strategies for you to implement.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 relative z-10">
              
              {/* INPUT FORM - Spans 1 column on large screens if results exist, else centered */}
              <div className={`lg:col-span-${results ? '1' : '3'} transition-all duration-500`}>
                  <form onSubmit={generateAudit} className={`space-y-6 ${!results ? 'max-w-md mx-auto' : ''}`}>
                    <div>
                    <label className="block text-sm font-semibold text-slate-400 mb-2 ml-1">Your Industry</label>
                    <input 
                        required 
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        type="text" 
                        className="w-full bg-slate-900/50 border border-slate-700 text-white px-4 py-3 rounded-[15px] focus:outline-none focus:border-[#25789a] focus:shadow-[0_0_15px_rgba(37,120,154,0.3)] transition-all placeholder-slate-600 font-light" 
                        placeholder="e.g. Real Estate, E-commerce..." 
                    />
                    </div>
                    <div>
                    <label className="block text-sm font-semibold text-slate-400 mb-2 ml-1">Current Bottleneck</label>
                    <textarea 
                        required 
                        value={bottleneck}
                        onChange={(e) => setBottleneck(e.target.value)}
                        rows="3" 
                        className="w-full bg-slate-900/50 border border-slate-700 text-white px-4 py-3 rounded-[15px] focus:outline-none focus:border-[#25789a] focus:shadow-[0_0_15px_rgba(37,120,154,0.3)] transition-all placeholder-slate-600 font-light" 
                        placeholder="e.g. I spend 2 hours a day manually copying leads from email to Excel..." 
                    />
                    </div>
                    <button 
                    type="submit" 
                    disabled={loading}
                    className="w-full py-4 bg-gradient-to-r from-[#25789a] to-[#1e6280] text-white font-bold rounded-[15px] transition-all duration-300 hover:shadow-[0_0_20px_#25789a] hover:scale-[1.02] flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed border border-transparent hover:border-[#4ab3d5]"
                    >
                    {loading ? <Loader2 className="animate-spin" /> : <Sparkles size={20} />}
                    {loading ? "Analysing..." : "Generate Strategies"}
                    </button>
                    {error && <p className="text-red-400 text-sm text-center">{error}</p>}
                </form>
              </div>

              {/* RESULTS DISPLAY - Grid of Cards */}
              {results && (
                  <div className="lg:col-span-2 grid gap-4 animate-fade-in">
                      {results.map((item, idx) => (
                          <div key={idx} className="bg-slate-900/80 border border-slate-700 rounded-[15px] p-5 hover:border-[#25789a] transition-all duration-300 shadow-lg">
                              <div className="flex items-center gap-3 mb-3">
                                  <div className="p-2 rounded-full bg-[#25789a]/20 text-[#25789a]">
                                      {idx === 0 ? <Wrench size={18} /> : idx === 1 ? <Lightbulb size={18} /> : <CheckSquare size={18} />}
                                  </div>
                                  <h4 className="font-bold text-white text-lg">{item.tool}</h4>
                              </div>
                              <div className="mb-3">
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Strategy</p>
                                  <p className="text-slate-300 text-sm font-light leading-relaxed">{item.strategy}</p>
                              </div>
                              <div>
                                  <p className="text-xs font-bold text-[#25789a] uppercase tracking-wider mb-1">Benefit</p>
                                  <p className="text-white text-sm font-medium leading-relaxed">{item.benefit}</p>
                              </div>
                          </div>
                      ))}
                  </div>
              )}
              
              {!results && !loading && (
                  <div className="hidden lg:flex lg:col-span-3 flex-col items-center justify-center text-slate-600 space-y-4 mt-8 pt-8 border-t border-slate-800/50">
                    <div className="w-20 h-20 rounded-full bg-slate-800/50 flex items-center justify-center">
                      <Zap size={40} className="opacity-20" />
                    </div>
                    <p className="text-sm font-light">Your tailored automation roadmap will appear here.</p>
                  </div>
              )}

            </div>
          </div>
        </FadeInSection>
      </div>
    </section>
  );
};

const Integrations = () => {
  const logos = [
    { 
      name: "WhatsApp", 
      url: "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" 
    },
    { 
      name: "Google", 
      url: "https://www.vectorlogo.zone/logos/google/google-icon.svg" 
    },
    { 
      name: "Microsoft", 
      url: "https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg" 
    },
    { 
      name: "OpenAI", 
      url: "https://upload.wikimedia.org/wikipedia/commons/4/4d/OpenAI_Logo.svg",
      className: "brightness-0 invert" // Make white for dark theme
    },
    { 
      name: "Slack", 
      url: "https://upload.wikimedia.org/wikipedia/commons/d/d5/Slack_icon_2019.svg" 
    },
    { 
      name: "Xero", 
      url: "https://www.vectorlogo.zone/logos/xero/xero-icon.svg" 
    },
  ];

  // Duplicate list for seamless loop
  const allLogos = [...logos, ...logos];

  return (
    <section className="py-16 bg-slate-900/50 border-y border-slate-800 overflow-hidden relative">
      <div className="max-w-7xl mx-auto px-6 mb-10 text-center">
        <p className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-2">Powering Your Workflow</p>
        <h3 className="text-xl font-semibold text-white">Seamlessly Integrated With Your Favorite Tools</h3>
      </div>
      
      <div className="flex w-full overflow-hidden relative py-10">
        {/* Gradient Masks for Fade Effect */}
        <div className="absolute left-0 top-0 bottom-0 w-64 z-10 bg-gradient-to-r from-[#020617] to-transparent" />
        <div className="absolute right-0 top-0 bottom-0 w-64 z-10 bg-gradient-to-l from-[#020617] to-transparent" />

        <div className="flex animate-scroll whitespace-nowrap pause-on-hover items-center">
          {allLogos.map((logo, index) => (
            <div key={index} className="mx-8 flex flex-col items-center justify-center min-w-[140px] group cursor-default">
              <div 
                className="h-12 w-auto flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
              >
                <img 
                  src={logo.url} 
                  alt={logo.name} 
                  className={`h-full w-auto object-contain ${logo.className || ''}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Benefits = () => {
    // Desktop layout is a Grid, Mobile layout is now a Vertical Stack.
    const benefitsData = [
        {
            icon: <AnimatedClock />,
            title: "Save Time",
            list: [
                "Automate repetitive tasks & follow-ups",
                "Focus on creative & strategic work",
                "No more manual lead chasing"
            ]
        },
        {
            icon: <AnimatedTrend />,
            title: "Boost Conversions",
            list: [
                "Instant responses at optimal times",
                "Never miss an opportunity",
                "Proven to boost conversion rates"
            ]
        },
        {
            icon: <AnimatedPulse />,
            title: "Always On",
            list: [
                "24/7 Operation, no holidays",
                "Consistent lead engagement",
                "Uninterrupted support"
            ]
        },
        {
            icon: <AnimatedMessage />,
            title: "Custom Outreach",
            list: [
                "Tailored to lead interests",
                "Personalised communications",
                "Drives deeper engagement"
            ]
        }
    ];
    
    return (
        <section id="benefits" className="relative py-20 px-6 z-10 scroll-mt-28">
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full max-w-4xl bg-[#25789a]/5 blur-[120px] rounded-full pointer-events-none" />

            <div className="max-w-7xl mx-auto relative">
                <FadeInSection>
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-[#FEFFFE] mb-6">
                            The <span className="text-[#25789a]">quantM</span> Advantage
                        </h2>
                        <p className="text-lg font-light leading-relaxed max-w-3xl mx-auto text-slate-300">
                            Results that save time, drive revenue, and reduce manual work - all on autopilot.
                        </p>
                    </div>
                </FadeInSection>

                {/* Desktop Layout: 4-Column Grid (Unchanged) */}
                <div className="hidden lg:grid grid-cols-4 gap-6">
                    {benefitsData.map((benefit, index) => (
                        <FadeInSection key={index} delay={index * 150} threshold={0.5} className="h-full">
                            <div className="h-full bg-slate-900/40 backdrop-blur-md p-6 rounded-[20px] border border-[#25789a]/50 transition-all duration-300 hover:border-[#25789a] hover:shadow-[0_0_15px_#25789a] hover:-translate-y-1 flex flex-col items-center group">
                                <div className="text-[#25789a] mb-6 mt-2 group-hover:scale-110 transition-transform duration-300">
                                    {benefit.icon}
                                </div>
                                <h3 className="text-lg font-bold uppercase mb-4 text-white tracking-wide">{benefit.title}</h3>
                                <div className="text-left w-full text-slate-300 font-light text-sm leading-relaxed">
                                    <ul className="list-none space-y-3">
                                        {benefit.list.map((item, i) => (
                                            <li key={i} className="relative pl-5">
                                                <span className="absolute left-0 text-[#25789a]">•</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </FadeInSection>
                    ))}
                </div>

                {/* Mobile/Tablet Layout: Vertical Stack */}
                {/* Replaced narrow carousel with full-width stacked cards for better readability */}
                <div className="lg:hidden flex flex-col gap-6">
                    {benefitsData.map((benefit, index) => (
                        <FadeInSection key={index} delay={index * 100}>
                            <div className="w-full bg-slate-900/60 backdrop-blur-md p-6 rounded-[20px] border border-[#25789a]/30 shadow-lg flex flex-col items-center text-center">
                                <div className="text-[#25789a] mb-4">
                                    {benefit.icon}
                                </div>
                                <h3 className="text-xl font-bold uppercase mb-4 text-white tracking-wide">{benefit.title}</h3>
                                
                                <div className="w-full bg-slate-950/50 rounded-xl p-4 border border-white/5">
                                    <ul className="list-none space-y-3 text-left">
                                        {benefit.list.map((item, i) => (
                                            <li key={i} className="relative pl-6 text-slate-300 text-sm font-light">
                                                <span className="absolute left-0 top-[2px] text-[#25789a] font-bold">✓</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </FadeInSection>
                    ))}
                </div>

            </div>
        </section>
    );
};

const Process = () => {
  const steps = [
    {
      icon: <Search size={28} />,
      title: "Discovery & Insight",
      description: "We don't go into meetings blind. We research your industry and business beforehand. In our first consultation, we discuss your specific bottlenecks and aim to identify high-impact solutions immediately."
    },
    {
      icon: <ClipboardCheck size={28} />,
      title: "Strategy & Alignment",
      description: "We dive deeper into the proposed solutions, narrowing them down to the 1-2 most critical workflows. We handle the formalities (agreements) efficiently so we can focus on the build."
    },
    {
      icon: <Workflow size={28} />,
      title: "Rapid Prototyping",
      description: "We build a functional prototype, typically using n8n, to showcase the system's logic and capabilities. You get to see the solution in action before we commit to code."
    },
    {
      icon: <Code size={28} />,
      title: "Custom Engineering",
      description: "Once the prototype is approved, we build the production-grade system using custom Python code. This ensures maximum speed, security, and independence from expensive third-party tools."
    },
    {
      icon: <Rocket size={28} />,
      title: "Launch & Trial",
      description: (
        <span>
          We deploy the system directly onto your infrastructure using your own API keys. You get a{" "}
          <span className="text-[#25789a] font-extrabold bg-[#25789a]/10 px-1 rounded">1-month free trial with no commitment</span>, 
          followed by a simple subscription model.
        </span>
      )
    }
  ];

  return (
    <section id="process" className="relative py-24 px-6 z-10 scroll-mt-28 bg-[#020617]">
      <div className="max-w-6xl mx-auto">
        <FadeInSection>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#FEFFFE] mb-6">
              How We <span className="text-[#25789a]">Work</span>
            </h2>
            <p className="text-lg font-light text-slate-400 max-w-2xl mx-auto">
              From initial discovery to deployment on your own infrastructure, our process is transparent, secure, and risk-free.
            </p>
          </div>
        </FadeInSection>

        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-[28px] md:left-1/2 top-0 bottom-0 w-[2px] bg-slate-800 md:-translate-x-1/2"></div>

          <div className="space-y-12">
            {steps.map((step, index) => (
              <FadeInSection key={index} delay={index * 150}>
                <div className={`flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-0 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  
                  {/* Content Card */}
                  <div className={`w-full md:w-1/2 pl-16 md:pl-0 ${index % 2 === 0 ? 'md:pr-16 md:pl-6' : 'md:pl-16 md:pr-6'}`}>
                    <div className={`bg-slate-900/40 backdrop-blur-sm border border-slate-800 p-6 rounded-[20px] hover:border-[#25789a] transition-colors duration-300 relative ${index % 2 === 0 ? 'text-left' : 'text-left md:text-right'}`}>
                      <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                      <div className="text-slate-400 font-light leading-relaxed text-sm">
                        {step.description}
                      </div>
                      
                      {/* Mobile-only connector (horizontal line to dot) */}
                      <div className="md:hidden absolute top-8 -left-[40px] w-[24px] h-[2px] bg-[#25789a]"></div>
                    </div>
                  </div>

                  {/* Icon/Dot Centerpiece */}
                  <div className="absolute left-0 md:left-1/2 -translate-x-0 md:-translate-x-1/2 flex items-center justify-center w-14 h-14 rounded-full bg-[#020617] border-2 border-[#25789a] text-[#25789a] z-10 shadow-[0_0_15px_rgba(37,120,154,0.4)]">
                    {step.icon}
                  </div>
                  
                  {/* Empty Spacer for the other side */}
                  <div className="hidden md:block w-1/2"></div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>

        <FadeInSection>
           <div className="mt-20 text-center">
             <ScrollLink 
               to="contact" 
               className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#25789a] to-cyan-600 text-white font-bold text-lg shadow-[0_0_30px_rgba(37,120,154,0.4)] border border-white/10 transform hover:scale-105 transition-all duration-300 cursor-pointer"
             >
                <CheckCircle size={24} className="text-white animate-pulse" />
                <span>1 Month Free Trial • No Commitment</span>
             </ScrollLink>
             <p className="mt-4 text-slate-400 font-light text-sm">Experience the value risk-free before you pay.</p>
           </div>
        </FadeInSection>
      </div>
    </section>
  );
};

const Pricing = () => {
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section id="pricing" className="relative py-24 px-6 z-10 scroll-mt-28 bg-[#020617]">
      {/* Background Ambience */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/0 via-slate-900/50 to-slate-900/0 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        <FadeInSection>
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-1 rounded-full bg-[#25789a]/10 border border-[#25789a]/30 text-[#25789a] text-xs font-bold tracking-widest uppercase mb-4">
              Pricing Plans
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6">
              Simple & <span className="text-[#25789a]">Transparent</span> Pricing
            </h2>
            <p className="text-lg text-slate-400 font-light mb-8 max-w-2xl mx-auto">
              We keep it simple. A one-time setup fee to build your system, followed by a monthly fee for hosting, maintenance, and support. <br/>
              <ScrollLink to="services" className="text-[#25789a] font-medium hover:underline cursor-pointer transition-colors inline-flex items-center gap-1 mt-2">
                  View specific system prices in our Services section <ArrowRight size={14} />
              </ScrollLink>
            </p>

            {/* Annual Toggle */}
            <div className="flex items-center justify-center gap-4 mb-8">
              <span className={`text-sm font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-slate-500'}`}>Monthly</span>
              <button 
                onClick={() => setIsAnnual(!isAnnual)}
                className="relative w-14 h-8 rounded-full bg-slate-800 border border-slate-600 focus:outline-none transition-colors"
              >
                <div className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-[#25789a] transition-transform duration-300 ${isAnnual ? 'translate-x-6' : 'translate-x-0'}`} />
              </button>
              <span className={`text-sm font-medium transition-colors ${isAnnual ? 'text-white' : 'text-slate-500'}`}>
                Annual <span className="text-[#25789a] text-xs font-bold ml-1">(2 Months Free)</span>
              </span>
            </div>
          </div>
        </FadeInSection>

        <FadeInSection>
            <div className="max-w-5xl mx-auto">
                {/* Unified Pricing Card */}
                <div className="relative flex flex-col md:flex-row overflow-hidden rounded-[24px] border border-[#25789a] bg-slate-900/80 shadow-[0_0_40px_rgba(37,120,154,0.15)]">
                    
                    {/* OTP Section */}
                    <div className="flex-1 p-8 md:p-12 border-b md:border-b-0 md:border-r border-slate-700/50 flex flex-col items-center md:items-start text-center md:text-left relative group">
                        <div className="absolute inset-0 bg-gradient-to-b from-[#25789a]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="relative z-10">
                            <div className="inline-flex p-3 rounded-2xl bg-[#25789a]/10 text-[#25789a] mb-6">
                                <Wrench size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">One-Time Setup (OTP)</h3>
                            <p className="text-slate-400 font-light text-sm mb-6 leading-relaxed">
                                Covers the design, custom development, API integration, and testing of your automation system.
                            </p>
                            
                            <div className="mt-auto">
                                <div className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-2">
                                    {isAnnual ? "£75 - £150" : "£150 - £300"}
                                </div>
                                <div className="h-6">
                                    {isAnnual ? (
                                        <span className="inline-block px-2 py-0.5 rounded bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-wide animate-pulse">
                                            50% Setup Discount Applied
                                        </span>
                                    ) : (
                                        <span className="text-xs text-slate-500 font-medium">Standard Setup Fee</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Maintenance Section */}
                    <div className="flex-1 p-8 md:p-12 flex flex-col items-center md:items-start text-center md:text-left relative group">
                        <div className="absolute inset-0 bg-gradient-to-b from-[#25789a]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="relative z-10">
                            <div className="inline-flex p-3 rounded-2xl bg-[#25789a]/10 text-[#25789a] mb-6">
                                <Zap size={32} />
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Maintenance Fee</h3>
                            <p className="text-slate-400 font-light text-sm mb-6 leading-relaxed">
                                Ensures your system stays online 24/7. Includes hosting, maintenance updates, and priority support.
                            </p>

                            <div className="mt-auto">
                                <div className="text-4xl md:text-5xl font-extrabold text-white tracking-tight mb-2">
                                    {isAnnual ? "£125 - £165" : "£150 - £200"}
                                    <span className="text-lg font-medium text-slate-500 ml-1">/ mo</span>
                                </div>
                                <div className="h-6">
                                    {isAnnual ? (
                                        <span className="inline-block px-2 py-0.5 rounded bg-[#25789a]/20 text-[#25789a] text-xs font-bold uppercase tracking-wide animate-pulse">
                                            Billed Annually (2 Months Free)
                                        </span>
                                    ) : (
                                        <span className="text-xs text-slate-500 font-medium">Monthly Rolling Contract</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Integrated Add-ons & Discounts (Moved Up) */}
                <div className="mt-8 max-w-3xl mx-auto flex flex-col gap-6 text-center">
                    
                    {/* Multi-system Discount Note */}
                    <div className="bg-slate-900/40 border border-slate-800 rounded-xl p-4 mx-auto w-full max-w-2xl">
                        <p className="text-slate-300 text-sm font-medium mb-1">💡 Multi-System Discount</p>
                        <p className="text-slate-400 text-xs font-light">
                            Scaling up? We offer special discounts when purchasing multiple systems together, or if you purchase one now and add another later.
                        </p>
                    </div>

                    <p className="text-slate-400 text-sm font-light">
                        Looking for something specific? We offer <span className="text-white font-medium">WhatsApp integration, Voice AI Agents, Custom Dashboards</span> and more. 
                        <ScrollLink to="contact" className="text-[#25789a] hover:underline ml-1">Contact us for a bespoke quote.</ScrollLink>
                    </p>
                </div>

                {/* Call to Action */}
                <div className="mt-8 text-center">
                    <ScrollLink 
                      to="contact"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-[16px] font-bold text-center transition-all duration-300 bg-[#25789a] text-white hover:bg-[#1e6280] hover:shadow-[0_0_30px_rgba(37,120,154,0.4)] hover:-translate-y-1"
                    >
                      Get Your Custom Roadmap <ArrowRight size={18} />
                    </ScrollLink>
                    <p className="mt-4 text-slate-500 text-xs font-light">
                        Prices vary based on complexity. Book a consultation for an exact quote.
                    </p>
                </div>
            </div>
        </FadeInSection>
      </div>
    </section>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      name: "Sarah Jenkins",
      role: "Operations Director",
      quote: "quantM completely transformed our invoicing process. We went from spending 15 hours a week on manual entry to zero. The accuracy is spot on."
    },
    {
      name: "David Chen",
      role: "Founder",
      quote: "The lead generation agent they built is incredible. It qualifies leads 24/7 and books meetings directly into my calendar. Our conversion rate doubled in a month."
    },
    {
      name: "Marcus Thorne",
      role: "CEO",
      quote: "I was skeptical about AI, but quantM's consulting was top-notch. They built a secure, local LLM for our legal documents that keeps everything private. Highly recommended."
    }
  ];

  return (
    <section id="testimonials" className="relative py-24 px-6 z-10 scroll-mt-28">
      <div className="max-w-7xl mx-auto">
        <FadeInSection>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">
              Trusted by <span className="text-[#25789a]">Innovators</span>
            </h2>
            <p className="text-lg font-light text-slate-400 max-w-2xl mx-auto">
              Don't just take our word for it. Here's what forward-thinking businesses are achieving with our automation solutions.
            </p>
          </div>
        </FadeInSection>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((t, i) => (
            <FadeInSection key={i} delay={i * 150}>
              <div className="h-full p-8 rounded-[20px] bg-slate-900/40 border border-slate-800 hover:border-[#25789a] transition-all duration-300 relative group shadow-lg hover:shadow-[0_0_20px_rgba(37,120,154,0.15)]">
                <div className="absolute top-6 right-6 text-[#25789a] opacity-20 group-hover:opacity-100 transition-opacity duration-500">
                  <Quote size={40} />
                </div>
                
                <div className="mb-6 relative z-10">
                    {/* Star Rating Visualization */}
                    <div className="flex gap-1 mb-4 text-[#25789a]">
                        {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                        ))}
                    </div>
                    <p className="text-slate-300 italic leading-relaxed text-sm md:text-base">"{t.quote}"</p>
                </div>

                <div className="flex items-center gap-4 mt-auto pt-6 border-t border-slate-800/50">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#25789a] to-slate-900 flex items-center justify-center font-bold text-white text-lg shadow-[0_0_10px_rgba(37,120,154,0.3)]">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">{t.name}</h4>
                    <p className="text-[#25789a] text-xs font-medium uppercase tracking-wide">{t.role}</p>
                  </div>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const PrivacyFocus = () => {
  return (
    <section id="privacy" className="relative py-20 px-6 z-10 scroll-mt-28 bg-slate-900/20">
      <div className="max-w-7xl mx-auto">
        <FadeInSection>
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#FEFFFE] mb-4">
              Data Privacy & <span className="text-[#25789a]">Security</span>
            </h2>
            <p className="text-lg font-light leading-relaxed max-w-2xl mx-auto text-slate-300">
              We understand that data privacy is paramount. That's why we offer flexible deployment options that put you in complete control of your sensitive information.
            </p>
          </div>
        </FadeInSection>

        <div className="grid md:grid-cols-2 gap-8">
          <FadeInSection delay={100}>
            <div className="h-full p-8 rounded-[20px] bg-slate-900/60 border border-slate-700 hover:border-[#25789a] hover:shadow-[0_0_20px_rgba(37,120,154,0.2)] transition-all duration-300">
              <div className="text-[#25789a] mb-4">
                <Lock size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Local LLMs & Processing</h3>
              <p className="text-slate-400 font-light text-sm leading-relaxed">
                Don't trust the cloud? We can deploy powerful open-source models (like Llama 3 or Mistral) running entirely on your local hardware. No data ever leaves your premises.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={200}>
            <div className="h-full p-8 rounded-[20px] bg-slate-900/60 border border-slate-700 hover:border-[#25789a] hover:shadow-[0_0_20px_rgba(37,120,154,0.2)] transition-all duration-300">
              <div className="text-[#25789a] mb-4">
                <Code size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Custom Python Pipelines</h3>
              <p className="text-slate-400 font-light text-sm leading-relaxed">
                We move beyond visual tools like n8n for production. We build robust, custom Python scripts that eliminate third-party middleware, ensuring faster execution and stricter security.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={300}>
            <div className="h-full p-8 rounded-[20px] bg-slate-900/60 border border-slate-700 hover:border-[#25789a] hover:shadow-[0_0_20px_rgba(37,120,154,0.2)] transition-all duration-300">
              <div className="text-[#25789a] mb-4">
                <GitBranch size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Version Control & Storage</h3>
              <p className="text-slate-400 font-light text-sm leading-relaxed">
                Full transparency and ownership. Your code and data are stored locally or in your private GitHub repositories with rigorous version control and audit trails.
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={400}>
            <div className="h-full p-8 rounded-[20px] bg-slate-900/60 border border-slate-700 hover:border-[#25789a] hover:shadow-[0_0_20px_rgba(37,120,154,0.2)] transition-all duration-300">
              <div className="text-[#25789a] mb-4">
                <CheckCircle size={32} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">GDPR Compliance</h3>
              <p className="text-slate-400 font-light text-sm leading-relaxed">
                We design systems with privacy by default. We ensure full compliance with GDPR regulations, including data minimisation, right to access, and secure deletion protocols.
              </p>
            </div>
          </FadeInSection>
        </div>
      </div>
    </section>
  );
};

const About = () => {
  return (
    <section id="about" className="relative py-24 px-6 z-10 bg-slate-900/30 scroll-mt-28">
      <div className="max-w-4xl mx-auto text-center">
        <FadeInSection>
          <div className="inline-block p-4 rounded-full bg-[#25789a]/10 text-[#25789a] mb-6">
            <Cpu size={40} />
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-8">About <span className="text-[#25789a]">quantM</span></h2>
          <p className="text-xl text-slate-300 font-light leading-relaxed">
            At quantM Solutions AI, we empower small businesses with cutting-edge AI solutions. 
            Our mission is to deliver tailored AI consultancy, automation, and no-code/low-code 
            tools that unlock your business’s potential without needing technical expertise.
          </p>
        </FadeInSection>
      </div>
    </section>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: "How secure are quantM Solutions AI's services?", a: "We use enterprise-grade encryption and comply with GDPR standards. All solutions are deployed through secure platforms, and data privacy is ensured throughout." },
    { q: "Do I need technical expertise to use your AI tools?", a: "No, our no-code/low-code platforms are designed for ease of use. We provide guided setup and support to ensure seamless integration for all users." },
    { q: "Can your AI solutions be customised for my industry?", a: "Yes, we tailor AI agents and automation tools for industries like restaurants, e-commerce, real estate, education, and professional services." },
    { q: "What happens after I book a consultation?", a: "We assess your needs, propose a tailored solution, and guide you through implementation. You'll receive ongoing support to ensure success." },
    { q: "Are there any hidden fees for your services?", a: "No, all costs are transparent and provided upfront in writing before you commit. Payments are processed securely through trusted platforms." },
    { q: "Can your chatbots integrate with my existing systems?", a: "Yes, our chatbots are compatible with most platforms, including CRMs, e-commerce systems, and customer support tools, with easy integration processes." },
    { q: "What kind of support do you offer post-implementation?", a: "We provide ongoing technical support, regular updates, and performance optimisation to ensure your AI solutions remain effective." },
    { q: "How long does it take to deploy an AI solution?", a: "Deployment typically takes 1-4 weeks, depending on complexity. We provide a clear timeline during the consultation phase." },
    { q: "What if I need to adjust the solution after deployment?", a: "Our solutions are flexible. We offer customisation options and support to tweak or scale your AI tools as your business needs evolve." },
    { q: "Why choose quantM Solutions AI over other providers?", a: "We offer tailored, no-code/low-code AI solutions with rapid deployment, transparent pricing, and dedicated support, ensuring maximum value for your business.", highlight: true },
  ];

  return (
    <section id="faq" className="relative py-24 px-6 z-10 flex justify-center scroll-mt-28">
      <div className="w-full max-w-[860px] bg-slate-900/40 backdrop-blur-md rounded-[20px] p-8 md:p-12 relative z-10 border border-slate-800 shadow-xl">
        <FadeInSection>
          <h2 className="text-center text-3xl md:text-4xl font-extrabold text-[#25789a] mb-12 [text-shadow:0_0_10px_rgba(37,120,154,0.3),0_0_20px_rgba(37,120,154,0.2)]">
            Frequently Asked Questions (<span className="animate-color-pulse">FAQ</span>)
          </h2>
        </FadeInSection>
          
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FadeInSection key={index} delay={index * 100}>
              <div 
                className={`border-b border-[#25789a]/50 rounded-[12px] transition-colors duration-300 ${openIndex === index ? 'bg-slate-800/50' : 'hover:bg-slate-800/30'}`}
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex justify-between items-center text-left focus:outline-none bg-transparent border-none text-lg font-bold text-[#FEFFFE] group hover:text-[#25789a] transition-all duration-300"
                >
                  <span className={faq.highlight ? "italic" : ""}>
                    {faq.highlight ? (
                        <>Why choose <span className="text-[#25789a] italic">quantM Solutions AI</span> over other providers?</>
                    ) : faq.q}
                  </span>
                  <span className={`text-[#FEFFFE] transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                    <ChevronDown size={20} />
                  </span>
                </button>
                
                {/* CSS Grid Animation for Smooth Accordion */}
                <div 
                  className={`grid overflow-hidden transition-all duration-300 ease-in-out ${
                    openIndex === index ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="min-h-0">
                    <div className="px-6 pb-6 text-slate-300 font-light leading-relaxed text-base">
                      {faq.a}
                    </div>
                  </div>
                </div>
              </div>
            </FadeInSection>
          ))}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    reason: '',
    service: '',
    company: '',
    linkedin: '',
    message: ''
  });
  const [status, setStatus] = useState('');

  const reasons = [
    { value: 'consultancy', label: 'AI Consultancy' },
    { value: 'automation', label: 'Workflow Automation' },
    { value: 'chatbots', label: 'AI Chatbots & Agents' },
    { value: 'custom', label: 'Custom System Build' },
    { value: 'other', label: 'Other Enquiry' }
  ];

  const serviceOptions = {
    consultancy: ['Strategy Session', 'Roadmap Development', 'Audit'],
    automation: ['n8n Workflow Automation', 'Data Entry Automation', 'Email Automation (Gmail/Outlook)'],
    chatbots: ['Customer Support Bot', 'Lead Gen Agent', 'Internal Knowledge Base'],
    custom: ['Full Stack AI App', 'Internal Tool', 'SaaS MVP'],
    other: ['Partnership', 'General Question']
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'reason') {
      setFormData(prev => ({ ...prev, [name]: value, service: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
            firstName: '', lastName: '', email: '', reason: '', 
            service: '', company: '', linkedin: '', message: ''
        });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  const inputClasses = `w-full bg-slate-950 border border-slate-800 text-white px-4 py-3 rounded-[20px] focus:outline-none focus:border-[#25789a] focus:shadow-[0_0_20px_#25789a] transition-all duration-300 placeholder-slate-600 font-light`;
  const labelClasses = "block text-sm font-semibold text-slate-400 mb-2 ml-1";

  return (
    <section id="contact" className="relative py-24 px-6 z-10 scroll-mt-28">
      <div className="max-w-4xl mx-auto">
        <FadeInSection>
          <div className="bg-slate-900/80 backdrop-blur-md p-8 md:p-12 rounded-[20px] border border-slate-800 shadow-2xl">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-2 text-center">Start Your <span className="text-[#25789a]">Transformation</span></h2>
            <p className="text-slate-400 text-center mb-10 font-light">Tell us about your needs and we'll build the roadmap.</p>

            {status === 'success' ? (
              <div className="text-center py-12 animate-fade-in">
                <CheckCircle className="mx-auto text-[#25789a] w-16 h-16 mb-4" />
                <h3 className="text-2xl font-bold text-white">Message Received!</h3>
                <p className="text-slate-400 mt-2 font-light">We will get back to you within 24 hours.</p>
                <button onClick={() => setStatus('')} className="mt-6 text-[#25789a] hover:underline font-semibold">Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClasses}>First Name</label>
                    <input required name="firstName" value={formData.firstName} onChange={handleChange} type="text" className={inputClasses} placeholder="Jane" />
                  </div>
                  <div>
                    <label className={labelClasses}>Last Name</label>
                    <input required name="lastName" value={formData.lastName} onChange={handleChange} type="text" className={inputClasses} placeholder="Doe" />
                  </div>
                </div>

                <div>
                  <label className={labelClasses}>Email Address</label>
                  <input required name="email" value={formData.email} onChange={handleChange} type="email" className={inputClasses} placeholder="jane@company.com" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClasses}>Reason for Enquiry</label>
                    <select required name="reason" value={formData.reason} onChange={handleChange} className={`${inputClasses} appearance-none cursor-pointer`}>
                      <option value="" disabled>Select a reason...</option>
                      {reasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelClasses}>Specific Service</label>
                    <select 
                      name="service" 
                      value={formData.service} 
                      onChange={handleChange} 
                      className={`${inputClasses} appearance-none cursor-pointer ${!formData.reason && 'opacity-50 cursor-not-allowed'}`}
                      disabled={!formData.reason}
                    >
                      <option value="" disabled>Select specific service...</option>
                      {formData.reason && serviceOptions[formData.reason]?.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClasses}>Company (Optional)</label>
                    <input name="company" value={formData.company} onChange={handleChange} type="text" className={inputClasses} placeholder="Your Company Ltd." />
                  </div>
                  <div>
                    <label className={labelClasses}>LinkedIn (Optional)</label>
                    <input name="linkedin" value={formData.linkedin} onChange={handleChange} type="url" className={inputClasses} placeholder="https://linkedin.com/in/..." />
                  </div>
                </div>

                <div>
                  <label className={labelClasses}>Message</label>
                  <textarea required name="message" value={formData.message} onChange={handleChange} rows="4" className={inputClasses} placeholder="Tell us about your automation goals..." />
                </div>

                <button 
                  type="submit" 
                  disabled={status === 'sending'}
                  className="w-full py-4 mt-4 bg-[#25789a] text-white font-bold rounded-[20px] transition-all duration-300 hover:bg-[#1e6280] hover:shadow-[0_0_20px_#25789a] hover:scale-[1.01] flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-wait"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                  {!status && <Send size={20} />}
                </button>
                {status === 'error' && <p className="text-red-500 text-center mt-2 font-semibold">Something went wrong. Please try again.</p>}
              </form>
            )}
          </div>
        </FadeInSection>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="relative bg-slate-900/40 backdrop-blur-md pt-12 pb-8 px-6 z-10 text-center mt-20 text-[#FEFFFE] border-t border-slate-800">
      <FadeInSection>
        <div className="max-w-[1200px] mx-auto flex flex-col items-center">
          
          <ScrollLink to="hero" className="inline-block mx-auto mb-3 group">
             <div className="w-[150px] h-12 bg-slate-800 rounded-[20px] flex items-center justify-center border border-[#25789a] group-hover:scale-105 group-hover:shadow-[0_0_10px_rgba(37,120,154,0.7)] transition-all duration-200">
                <span className="text-[#25789a] font-extrabold text-xl">quantM</span>
             </div>
          </ScrollLink>

          <p className="text-[13px] text-[#FEFFFE] max-w-[700px] mx-auto mb-4 leading-[1.6] font-normal">
            "Empowering Small Businesses with AI-Driven Automation and Innovation"
          </p>

          <ul className="flex justify-center flex-wrap gap-[1.2rem] my-8 p-0 list-none">
            {['Home', 'Services', 'Benefits', 'Process', 'Pricing', 'About', 'FAQ', 'Contact'].map((item) => (
              <li key={item}>
                <ScrollLink 
                  to={item === 'Home' ? 'hero' : item.toLowerCase()} 
                  className="text-[#FEFFFE] text-[14px] font-semibold relative transition-colors duration-200 hover:text-[#2e95bf] group cursor-pointer"
                >
                  {item}
                  <span className="absolute left-0 -bottom-[3px] w-full h-[2px] bg-[#25789a] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </ScrollLink>
              </li>
            ))}
             <li>
                <a href="/terms-and-conditions" className="text-[#FEFFFE] text-[14px] font-semibold relative transition-colors duration-200 hover:text-[#2e95bf] group">
                    Terms and Conditions
                    <span className="absolute left-0 -bottom-[3px] w-full h-[2px] bg-[#25789a] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </a>
             </li>
             <li>
                <a 
                  href="https://cdn.prod.website-files.com/6876df5b300645740c295cb8/68ecf975f073b9e1b2b82872_quantM%20Privacy%20Policy.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#FEFFFE] text-[14px] font-semibold relative transition-colors duration-200 hover:text-[#2e95bf] group"
                >
                    Privacy Policy
                    <span className="absolute left-0 -bottom-[3px] w-full h-[2px] bg-[#25789a] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </a>
             </li>
             <li>
                <ScrollLink to="hero" className="text-[#FEFFFE] text-[14px] font-semibold relative transition-colors duration-200 hover:text-[#2e95bf] group cursor-pointer">
                    Back to Top
                    <span className="absolute left-0 -bottom-[3px] w-full h-[2px] bg-[#25789a] scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left" />
                </ScrollLink>
             </li>
          </ul>

          <div className="flex justify-center gap-6 mb-5">
             <a href="https://www.linkedin.com/company/quantm-solutions-ai" target="_blank" rel="noreferrer" className="text-white hover:text-[#25789a] hover:scale-110 hover:shadow-[0_0_10px_rgba(37,120,154,0.7)] transition-all duration-200">
               <Linkedin size={24} />
             </a>
             <a href="https://www.instagram.com/quantm_solutions_ai" target="_blank" rel="noreferrer" className="text-white hover:text-[#25789a] hover:scale-110 hover:shadow-[0_0_10px_rgba(37,120,154,0.7)] transition-all duration-200">
               <Instagram size={24} />
             </a>
             <a href="mailto:info@quantmsolutionsai.com" className="text-white hover:text-[#25789a] hover:scale-110 hover:shadow-[0_0_10px_rgba(37,120,154,0.7)] transition-all duration-200">
               <Mail size={24} />
             </a>
          </div>

          <div className="text-[13px] text-[#aaa] mb-5 font-light">
             <span>
               📩 <a href="mailto:info@quantmsolutionsai.com" className="text-[#25789a] hover:text-[#2e95bf] transition-colors font-medium">info@quantmsolutionsai.com</a> | 📍 United Kingdom
             </span>
          </div>

          <div className="text-[12px] border-t border-[#25789a] pt-3 text-[#888] mt-6 w-full font-light">
            © {new Date().getFullYear()} <span className="text-[#25789a] animate-color-pulse font-semibold">quantM</span> Solutions AI. All rights reserved.
          </div>

        </div>
      </FadeInSection>
    </footer>
  );
};

// --- App Root ---

export default function App() {
  return (
    <div className="bg-slate-950 min-h-screen text-slate-200 selection:bg-[#25789a] selection:text-white">
      <GlobalStyles />
      <AbstractBackground />
      <Navbar />
      <Hero />
      <Services />
      <Integrations />
      <Benefits />
      <Process />
      <Testimonials />
      <AIAudit />
      <Pricing />
      <PrivacyFocus />
      <About />
      <FAQ />
      <Contact />
      <Footer />
    </div>
  );
}